import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { fromEvent, merge } from 'rxjs';
import { QueryParamsModel } from '../../../../../core/_base/crud';
import { MatDialog } from '@angular/material/dialog';
import { AdminAddTagsDialogComponent } from './admin-add-tags-dialog/admin-add-tags-dialog.component';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../../core/reducers';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { FormBuilder, FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { NavigationService } from '../../../../../core/services/navigation.service';
import { ActivatedRoute } from '@angular/router';
import { TagsPageRequested } from '../../../../../core/lists/_actions/tag.actions';
import { TagsDataSource } from '../../../../../core/lists/_data-sources/tag.datasource';

@Component({
  selector: 'kt-tags-list',
  templateUrl: './tags-list.component.html',
  styleUrls: ['./tags-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagsListComponent implements OnInit, OnDestroy {
  dataSource: TagsDataSource;
  displayedColumns = ['name', 'type', 'frequency', 'actions'];
  searchBarColumns = ['searchName', 'searchType', 'searchFrequency', 'searchActions'];
  showSearchBar = false;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort', { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  searchNameInput = '';
  searchTypeInput = '';
  searchFrequencyInput = '';
  private subscriptions: any[] = [];
  searchForm: FormGroup;
  filtersApplied: any = false;
  firstLoadAfterBack: any = false;
  pageSize: any;
  typesList = [
    {
      label: 'All Entities',
      value: 'ALL_ENTITIES',
    },
    {
      label: 'Doctor',
      value: 'DOCTOR',
    },
    {
      label: 'Patient',
      value: 'PATIENT',
    },
    {
      label: 'Facility',
      value: 'FACILITY',
    },
    {
      label: 'Contact',
      value: 'CONTACT',
    },
  ];

  constructor(
    public dialog: MatDialog,
    private store: Store<AppState>,
    private activatedRoute: ActivatedRoute,
    private fb: FormBuilder,
    private navigationService: NavigationService,
  ) {}

  ngOnInit() {
    // Comment by Nourhan: please don't change the order of this line
    this.filtersApplied = this.navigationService.getFilters();

    const sortSubscription = this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = this.paginator?.pageIndex || 0;
      this.sort.active = this.sort.direction ? this.sort.active : 'id';
      this.sort.direction = this.sort.direction || 'desc';
    });
    this.subscriptions.push({ id: 1, sub: sortSubscription });

    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        tap(() => {
          this.loadTagsList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 2, sub: paginatorSubscriptions });

    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.loadTagsList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 3, sub: searchSubscription });

    this.dataSource = new TagsDataSource(this.store);

    this.searchForm = this.fb.group({
      search: [],
      status: ['true'],
    });

    // Comment by Nourhan: please don't change the order of this scope
    if (this.filtersApplied) {
      this.firstLoadAfterBack = true;
      this.pageSize = this.filtersApplied.pageSize;
    }

    const routeSubscription = this.activatedRoute.queryParams.subscribe(() => {
      this.loadTagsList();
    });
    this.subscriptions.push({ id: 5, sub: routeSubscription });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  openAddTagsDialog() {
    const dialogRef = this.dialog.open(AdminAddTagsDialogComponent, { width: '600px' });
    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.reload) {
        this.loadTagsList();
      }
    });
  }

  loadTagsList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'desc',
      this.sort.active || 'id',
      this.paginator.pageIndex,
      this.paginator.pageSize || this.pageSize || 25,
    );
    this.store.dispatch(new TagsPageRequested({ page: queryParams }));
  }

  filterConfiguration(reset_applied_flag?: any): any {
    const filter: any = {};
    if (this.filtersApplied && this.firstLoadAfterBack) {
      this.firstLoadAfterBack = false;
      const appliedFilters = this.filtersApplied;
      this.showSearchBar = appliedFilters.showSearchBar;
      if (this.showSearchBar) {
        this.searchNameInput = appliedFilters.name;
        this.searchTypeInput = appliedFilters.type;
        this.searchFrequencyInput = appliedFilters.frequency;
      } else {
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.value = appliedFilters.searchTerm || '';
        }
      }
      this.sort.direction = this.filtersApplied?.sortDirection || 'desc';
      this.sort.active = this.filtersApplied?.sortActive || 'id';
      this.paginator.pageIndex = this.filtersApplied?.pageIndex || 0;
      this.paginator.pageSize = this.filtersApplied?.pageSize || 25;
    }
    if (reset_applied_flag) this.filtersApplied = false;
    if (this.showSearchBar) {
      filter.name = this.searchNameInput;
      filter.type = this.searchTypeInput;
      filter.frequency = this.searchFrequencyInput;
      filter.showSearchBar = true;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
      filter.showSearchBar = false;
    }
    filter.sortDirection = this.sort.direction || 'desc';
    filter.sortActive = this.sort.active || 'id';
    filter.pageIndex = this.paginator.pageIndex;
    filter.pageSize = this.paginator.pageSize;
    return filter;
  }

  reloadTagsList(): void {
    this.paginator.pageIndex = 0;
    this.loadTagsList();
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();
    this.searchNameInput = '';
    this.searchTypeInput = '';
    this.searchFrequencyInput = '';
  }

  resetSearchInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadTagsList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
    } else {
      this.resetSearchBar();
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadTagsList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();
    this.reloadTagsList();
  }
}
